<script lang="ts">
    import {Button} from "@/components/ui/button";
    import {Download, RotateCw, Trash2, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Pencil, Check, X} from "lucide-svelte";
    import type {Image} from "@/types/types";
    import {router} from "@inertiajs/svelte";
    import {toast} from "svelte-sonner";

    type Props = {
        title?: string;
        onClose: () => void;
        image?: Image;
        onNext?: () => void;
        onPrevious?: () => void;
        currentIndex?: number;
        totalImages?: number;
        onDescriptionUpdate?: (imageId: string, description: string | null) => void;
    };

    let { title = '', onClose, image, onNext, onPrevious, currentIndex, totalImages, onDescriptionUpdate }: Props = $props();

    // Description editing state
    let isEditingDescription = $state(false);
    let editedDescription = $state('');
    let isSavingDescription = $state(false);

    // Reset edit state when image changes
    $effect(() => {
        if (image) {
            editedDescription = image.description ?? '';
            isEditingDescription = false;
        }
    });

    function startEditingDescription() {
        editedDescription = image?.description ?? '';
        isEditingDescription = true;
    }

    function cancelEditingDescription() {
        editedDescription = image?.description ?? '';
        isEditingDescription = false;
    }

    async function saveDescription() {
        if (!image?.id) return;

        isSavingDescription = true;
        try {
            const response = await fetch(route('images.update', { imageId: image.id }), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    description: editedDescription.trim() || null,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                // Update local image state
                if (image) {
                    image.description = data.image.description;
                }
                // Notify parent component
                if (onDescriptionUpdate) {
                    onDescriptionUpdate(image.id, data.image.description);
                }
                toast.success('Description updated');
                isEditingDescription = false;
            } else {
                toast.error('Failed to update description');
            }
        } catch (error) {
            console.error('Error saving description:', error);
            toast.error('Failed to update description');
        } finally {
            isSavingDescription = false;
        }
    }

    let mouseDownOnBackdrop = false;

    function handleMouseDown(e: MouseEvent) {
        mouseDownOnBackdrop = e.target === e.currentTarget;
    }

    function handleMouseUp(e: MouseEvent) {
        if (mouseDownOnBackdrop && e.target === e.currentTarget) {
            onClose();
        }
        mouseDownOnBackdrop = false;
    }

    function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowRight' && onNext) {
            onNext();
            resetZoomAndRotation();
        } else if (e.key === 'ArrowLeft' && onPrevious) {
            onPrevious();
            resetZoomAndRotation();
        }
    }

    let previewZoom = $state(100);
    let previewRotation = $state(0);

    function zoomIn() {
        previewZoom = Math.min(previewZoom + 25, 200);
    }

    function zoomOut() {
        previewZoom = Math.max(previewZoom - 25, 25);
    }

    function rotate() {
        previewRotation = (previewRotation + 90) % 360;
    }

    function resetZoomAndRotation() {
        previewZoom = 100;
        previewRotation = 0;
    }

    function handleNext() {
        if (onNext) {
            onNext();
            resetZoomAndRotation();
        }
    }

    function handlePrevious() {
        if (onPrevious) {
            onPrevious();
            resetZoomAndRotation();
        }
    }

    // Delete image function
    function deleteImage() {
        if (confirm('Are you sure you want to delete this image?')) {
            router.delete(route('images.destroy', {imageId: image?.id}), {
                preserveScroll: true,
                onSuccess: () => {
                    toast.success('Image deleted successfully');
                    onClose(); // Close modal after deletion
                },
                onError: () => {
                    toast.error('Failed to delete image');
                }
            });
        }
    }

</script>

<div
    role="dialog"
    aria-modal="true"
    class="fixed inset-0 flex items-center justify-center bg-black/70 z-50"
    onmousedown={handleMouseDown}
    onmouseup={handleMouseUp}
    onkeydown={handleKeyDown}
    tabindex="0"
>
    <!-- Previous Button -->
    {#if onPrevious}
        <button
            onclick={handlePrevious}
            class="nav-button nav-button-left"
            aria-label="Previous image"
        >
            <ChevronLeft class="h-8 w-8" />
        </button>
    {/if}

    <div class="bg-card flex flex-col gap-y-4 rounded-lg shadow-lg p-4 m-8 sm:m-4 md:m-0 w-auto max-h-[90vh] max-w-[90vw] overflow-y-auto">
        {#if title}
            <h2 class="text-lg font-bold text-center">{title}</h2>
        {/if}

        <!-- Image counter -->
        {#if currentIndex !== undefined && totalImages !== undefined}
            <div class="text-center text-sm text-muted-foreground">
                {currentIndex + 1} / {totalImages}
            </div>
        {/if}

        <div class="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" onclick={zoomOut} disabled={previewZoom <= 25}>
                <ZoomOut class="h-4 w-4" />
            </Button>
            <span class="text-sm px-2">{previewZoom}%</span>
            <Button variant="outline" size="sm" onclick={zoomIn} disabled={previewZoom >= 200}>
                <ZoomIn class="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onclick={rotate}>
                <RotateCw class="h-4 w-4" />
            </Button>
            <a href={route('images.download', {imageId: image?.id})} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                    <Download class="h-4 w-4" />
                </Button>
            </a>

            <Button variant="outline" size="sm" onclick={deleteImage}>
                <Trash2 class="h-4 w-4" />
            </Button>
        </div>

        <div class="overflow-auto flex justify-center p-4">
            <img src={`/images/fullImage/${image?.id}`} alt={image?.description || image?.filename || 'Photo'}
                 class="block"
                 style="transform: scale({previewZoom / 100}) rotate({previewRotation}deg);" />
        </div>

        <!-- Description section -->
        <div class="description-section border-t pt-3 mt-2">
            {#if isEditingDescription}
                <div class="flex flex-col gap-2">
                    <textarea
                        bind:value={editedDescription}
                        placeholder="Add a description..."
                        class="w-full p-2 text-sm border rounded-md resize-none bg-background"
                        rows="2"
                        maxlength="1000"
                    ></textarea>
                    <div class="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onclick={cancelEditingDescription} disabled={isSavingDescription}>
                            <X class="h-4 w-4 mr-1" />
                            Cancel
                        </Button>
                        <Button variant="default" size="sm" onclick={saveDescription} disabled={isSavingDescription}>
                            <Check class="h-4 w-4 mr-1" />
                            {isSavingDescription ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            {:else}
                <div class="flex items-start gap-2">
                    <div class="flex-1 text-sm text-muted-foreground">
                        {#if image?.description}
                            <p>{image.description}</p>
                        {:else}
                            <p class="italic">No description</p>
                        {/if}
                    </div>
                    <Button variant="ghost" size="sm" onclick={startEditingDescription}>
                        <Pencil class="h-4 w-4" />
                    </Button>
                </div>
            {/if}
        </div>
    </div>

    <!-- Next Button -->
    {#if onNext}
        <button
            onclick={handleNext}
            class="nav-button nav-button-right"
            aria-label="Next image"
        >
            <ChevronRight class="h-8 w-8" />
        </button>
    {/if}
</div>

<style>
    .nav-button {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: rgba(0, 0, 0, 0.5);
        color: white;
        border: none;
        border-radius: 50%;
        width: 3rem;
        height: 3rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 60;
    }

    .nav-button:hover {
        background: rgba(0, 0, 0, 0.8);
        transform: translateY(-50%) scale(1.1);
    }

    .nav-button:active {
        transform: translateY(-50%) scale(0.95);
    }

    .nav-button-left {
        left: 2rem;
    }

    .nav-button-right {
        right: 2rem;
    }

    @media (max-width: 640px) {
        .nav-button-left {
            left: 1rem;
        }

        .nav-button-right {
            right: 1rem;
        }

        .nav-button {
            width: 2.5rem;
            height: 2.5rem;
        }
    }

    .description-section {
        min-width: 300px;
        max-width: 100%;
    }

    .description-section textarea {
        font-family: inherit;
    }
</style>
